import { useState, useEffect, type ReactNode } from "react";
import { Check, Sparkles } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Container } from "./ui";
import { Button } from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import { type Plan, price, formatMoney, TRIAL_URL, localizedPlans, countryFor } from "../content/pricing";
import { featureLiveForMarket } from "../content/flags";

/**
 * PricingTable — the rich Done-it pricing (free tier, base + per-user, market-
 * aware CIAO@work, coming-soon block) rendered with the reference component's
 * behaviour: cards fan out on scroll-in (spring whileInView — sides scaled down
 * & nudged inward, popular card lifted), the price rolls on toggle, the toggle
 * pill slides. The card, toggle and breakpoint hook are exported so PricingPlans
 * (home) can compose Time + Project families under one shared toggle.
 */

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

// Rolling text (NumberFlow stand-in): old value slides up/out, new one rolls in.
function Roll({ text, className }: { text: string; className?: string }) {
  return (
    <span className={cn("relative inline-flex overflow-hidden", className)}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={text}
          initial={{ y: "55%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-55%", opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {text}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export function PricingPlanCard({
  plan, annual, index, isDesktop, fan = true, className, currency = "eur", cta, compact = false, headerOnly = false, bento = false,
}: {
  plan: Plan; annual: boolean; index: number; isDesktop: boolean;
  /** Fan-out arrangement (3-card Time family). Off → plain fade-up (e.g. Project). */
  fan?: boolean;
  className?: string;
  /** ISO currency for the baked per-market price (DIT-264 / S9). Default EUR. */
  currency?: string;
  /** Override the default "start free trial" CTA — e.g. the signup funnel injects
   *  a "continue with this plan" button. When omitted the marketing trial CTA renders. */
  cta?: ReactNode;
  /** Tighter vertical card (used when many cards sit in one row — the funnel's
   *  5-across plan step): always column, smaller padding + price. */
  compact?: boolean;
  /** Header only — name + tagline + popular badge + price. Drops the CTA and the
   *  whole feature/coming-soon group. Used as a bento tile in the funnel plan step,
   *  where the feature detail lives in the comparison table below. */
  headerOnly?: boolean;
  /** Bento-tile chrome (funnel): grey `bg-bg-subtle` surface, no shadow, tighter
   *  `rounded-2xl` — so tiles sit inside one framed panel like the marketing
   *  "What's inside Time" bento. Popular keeps its accent gradient (shadow off). */
  bento?: boolean;
}) {
  const { locale, market, t } = useLocale();
  const pop = plan.popular;
  const priceSize = compact ? "text-4xl" : "text-5xl";
  const reduce = useReducedMotion();
  // The per-user / admins note, moved out of the price block into the feature list.
  const payNote = plan.free ? null : plan.admins ? t("pricing:card.adminsIncluded", { count: plan.admins }) : t("pricing:card.payOnlyTracked");

  // The fanned arrangement from the reference: sides shrink and slide inward,
  // the popular (middle) card lifts. Desktop + fan only; else just fades in.
  const target = isDesktop && fan
    ? { opacity: 1, y: pop ? -20 : 0, x: index === 2 ? -30 : index === 0 ? 30 : 0, scale: index === 0 || index === 2 ? 0.94 : 1 }
    : { opacity: 1, y: 0, x: 0, scale: 1 };

  const check = (label: string, key?: string) => (
    <li key={key ?? label} className="flex items-start gap-3">
      <Check size={16} strokeWidth={2.5} className={cn("mt-0.5 shrink-0", pop ? "text-white" : "text-[var(--ds-accent-deep)]")} />
      <span className={cn("text-ds-14", pop ? "text-white/90" : "text-ds-ink")}>{label}</span>
    </li>
  );

  // Funnel usage (headerOnly bento tiles) is a selection UI, not a marketing
  // hero — the 1.2s stagger-in reveal reads as jank there and blows the
  // sub-300ms UI budget, so those tiles render statically. The fanned scroll-in
  // stays for the marketing pricing surfaces.
  const noReveal = reduce || headerOnly;
  return (
    <motion.div
      initial={noReveal ? false : { y: 50, opacity: 0 }}
      whileInView={noReveal ? undefined : target}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 100, damping: 30, duration: 1.2, delay: 0.15 + index * 0.12, opacity: { duration: 0.5 } }}
      className={cn(
        // vertical (mobile) → horizontal inside the card (tablet) → vertical (desktop grid).
        // `compact` keeps it a tight vertical column at every width (5-across funnel).
        compact
          ? "relative flex h-full flex-col gap-6 p-4 md:p-5"
          : "relative flex h-full flex-col gap-8 p-4 md:p-8 md:flex-row md:items-stretch md:gap-10 lg:flex-col lg:gap-8",
        bento ? "rounded-2xl" : "rounded-3xl",
        pop
          // Bento (funnel) keeps no shadow but gets a darker-blue hairline outline
          // so the gradient card reads as a defined tile against the white panel
          // instead of blending. `outline` (not ring) so it composes with the
          // funnel's selection/hover ring.
          ? `z-10 overflow-hidden bg-[linear-gradient(to_bottom,var(--ds-accent-deep),var(--ds-accent))] text-white${bento ? " outline outline-1 outline-[color:var(--ds-accent-deep)]" : " shadow-[0_24px_60px_-15px_rgba(63,116,207,0.55)]"}`
          : bento
            ? "z-0 bg-bg-subtle text-ds-ink"
            : "z-0 border border-line bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]",
        fan && !pop && "lg:mt-5",
        fan && index === 0 && "origin-right",
        fan && index === 2 && "origin-left",
        className,
      )}
    >
      {pop && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-[radial-gradient(rgba(255,255,255,0.28)_1px,transparent_1px)] bg-[size:12px_12px] [mask-image:linear-gradient(to_bottom,black,transparent)]"
        />
      )}

      {/* LEFT group — header + price + CTA (top on mobile/desktop, side on tablet) */}
      <div className="relative md:w-[42%] md:shrink-0 lg:w-full">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn("font-display text-ds-22 font-semibold tracking-tight", pop ? "text-white" : "text-ds-ink")}>{plan.name}</h3>
            <p className={cn("mt-1 text-ds-14", pop ? "text-white/80" : "text-ds-ink-soft")}>{t(`pricing:plan.${plan.id}.tagline`)}</p>
          </div>
          {pop && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-ds-gold px-3 py-1 text-[13px] font-semibold text-ds-ink">{t("pricing:card.mostPopular")}</span>
          )}
        </div>

        {/* Price — free vs base + per-user (the pay-note now lives in the features) */}
        <div className="mt-6">
          {plan.free ? (
            <div className="flex items-baseline gap-2">
              <span className={cn("font-display font-bold leading-none tracking-tight text-ds-ink", priceSize)}>{t("pricing:card.free")}</span>
              <span className="text-ds-14 font-medium text-ds-ink-soft">{t("pricing:card.forever")}</span>
            </div>
          ) : (
            <>
              {/* Per-user price leads: that's what a buyer compares plans on. The
                  base fee stays, one line down (client: "the price per user has
                  to be showed BIG and not the base/month"). */}
              <div className="flex items-baseline gap-1.5">
                <Roll text={formatMoney(price(plan.monthly!.user, annual), currency, locale)} className={cn("shrink-0 font-display font-bold leading-none tracking-tight", priceSize, pop ? "text-white" : "text-ds-ink")} />
                <span className={cn("text-ds-14 font-medium", pop ? "text-white/75" : "text-ds-ink-soft")}>{t("pricing:card.perUserSuffix")}</span>
              </div>
              <div className={cn("mt-2 text-ds-14", pop ? "text-white/85" : "text-ds-ink-soft")}>{t("pricing:card.plusBasePerMonth", { amount: formatMoney(price(plan.monthly!.base, annual), currency, locale) })}</div>
            </>
          )}
        </div>

        {!headerOnly && (
          <div className="mt-7">
            {cta ?? (
              <Button to={TRIAL_URL} variant={pop ? "outline" : "solid"} arrow={false} className="w-full">
                {plan.free ? t("pricing:card.ctaStartFree") : t("pricing:card.ctaStartFreeWith", { plan: plan.name })}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* RIGHT group — features + coming-soon (bottom on mobile/desktop, side on tablet) */}
      {!headerOnly && (
      <div className="relative flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-3.5">
          {payNote && check(payNote, "paynote")}
          {plan.features.map((feature) => check(t(`pricing:plan.${plan.id}.feature.${feature}`), feature))}
          {plan.checkinWork && market === "belgium" && featureLiveForMarket(market, "check_in_at_work") && check(t("pricing:card.checkinWork"), "ciao")}
        </ul>

        {plan.comingSoon && (
          <div className={cn("mt-6 rounded-2xl p-4", pop ? "bg-white/15" : "bg-bg-subtle")}>
            <div className={cn("flex items-center gap-1.5 text-[13px] font-semibold", pop ? "text-white" : "text-ds-ink-soft")}>
              <Sparkles size={14} strokeWidth={2} /> {t("pricing:card.comingSoonFree")}
            </div>
            <div className={cn("mt-1 text-[13px] leading-snug", pop ? "text-white/75" : "text-ds-ink-soft")}>{plan.comingSoon.map((c) => t(`pricing:plan.${plan.id}.comingSoon.${c}`)).join(" · ")}</div>
          </div>
        )}
      </div>
      )}
    </motion.div>
  );
}

/** Monthly / Annual toggle — the active pill slides (per-instance layout id). */
export function PricingToggle({ annual, setAnnual, className, id = "default" }: { annual: boolean; setAnnual: (v: boolean) => void; className?: string; id?: string }) {
  const { t } = useLocale();
  const segments = [
    { annual: false, label: <>{t("pricing:toggle.monthly")}</> },
    { annual: true, label: <>{t("pricing:toggle.annual")} <span className="font-semibold text-[var(--ds-accent-deep)]">{t("pricing:toggle.save")}</span></> },
  ];
  return (
    <div className={cn("flex", className)}>
      <div className="inline-flex items-center gap-1 rounded-full border border-line bg-bg-subtle p-1">
        {segments.map((seg) => {
          const active = annual === seg.annual;
          return (
            <button
              key={String(seg.annual)}
              onClick={() => setAnnual(seg.annual)}
              className="relative rounded-full px-5 py-2 text-ds-14 font-semibold"
            >
              {active && (
                <motion.span
                  layoutId={`pricing-toggle-pill-${id}`}
                  className="absolute inset-0 rounded-full bg-white shadow-sm"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className={cn("relative z-10 transition-colors", active ? "text-ds-ink" : "text-ds-ink-soft hover:text-ds-ink")}>{seg.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PricingTable({ plans }: { plans: Plan[] }) {
  const [annual, setAnnual] = useState(false);
  const isDesktop = useIsDesktop();
  const { locale } = useLocale();
  // Per-market currency, baked from the catalog at build (S9); EUR fallback.
  const { plans: localized, currency } = localizedPlans(plans, countryFor(locale));

  return (
    <Container className="py-16 font-ds md:py-20">
      <PricingToggle id="table" annual={annual} setAnnual={setAnnual} className="mb-14 justify-center" />
      <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
        {localized.map((plan, index) => (
          <PricingPlanCard key={plan.name} plan={plan} annual={annual} index={index} isDesktop={isDesktop} currency={currency} />
        ))}
      </div>
    </Container>
  );
}
